import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import useRoles from "../../../features/Roles/hooks/useRoles";
import { useEffect, useState } from "react";
import { 
  Button, 
  Center, 
  Flex, 
  Heading, 
  VStack, 
  Box,
  Text,
  Spinner,
  TableContainer,
  Table as ChakraTable,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  Image,
  Input,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  HStack,
  useToast
} from "@chakra-ui/react";
import { ROUTES } from "../../../consts/routes";
import { AdminLevelRewardAssignment, OlaggModule, PermissionEnum } from "@olagg/db-types";
import Pagination from "@olagg/ui-kit/Pagination";
import useLevelRewardAssignments from "./hooks/useLevelRewardAssignments";
import moment from "moment";


const AssignmentDetailsModal = ({ 
  assignment, 
  isOpen, 
  onClose 
}: { 
  assignment: AdminLevelRewardAssignment | null; 
  isOpen: boolean; 
  onClose: () => void;
}) => {
  if (!assignment) return null;

  const renderAssignmentData = () => {
    const data = assignment.assignmentData;
    const rewardType = assignment.rewardInfo.rewardType;

    switch (rewardType) {
      case 'xp_bonus':
        return (
          <VStack align="stretch" spacing={2}>
            <Text><strong>XP Agregado:</strong> {data.xpAdded}</Text>
            <Text><strong>Score Anterior:</strong> {data.previousScore}</Text>
            <Text><strong>Nuevo Score:</strong> {data.newScore}</Text>
          </VStack>
        );

      case 'blockchain_token':
        return (
          <VStack align="stretch" spacing={2}>
            <Text><strong>Token:</strong> {data.blockchainTokenName}</Text>
            <Text><strong>Cantidad:</strong> {data.quantity}</Text>
            <Text><strong>ID de Pago:</strong> {data.blockchainTokenPaymentId}</Text>
          </VStack>
        );

      case 'treasure_opportunity':
        return (
          <VStack align="stretch" spacing={2}>
            <Text><strong>Oportunidades Creadas:</strong> {data.opportunitiesCreated}</Text>
          </VStack>
        );

      case 'claw_coin':
        return (
          <VStack align="stretch" spacing={2}>
            <Text><strong>Monedas Creadas:</strong> {data.coinsCreated}</Text>
          </VStack>
        );

      case 'store_discount':
        return (
          <VStack align="stretch" spacing={2}>
            <Text><strong>Código:</strong> {data.code}</Text>
            <Text><strong>Porcentaje:</strong> {data.discountPercentage}%</Text>
            <Text><strong>Límite:</strong> {data.discountLimit}</Text>
            <Text><strong>Expira:</strong> {moment(data.expiresAt).format('DD/MM/YYYY')}</Text>
            <Text><strong>ID de Cupón:</strong> {data.couponId}</Text>
          </VStack>
        );

      case 'custom_reward':
        return (
          <VStack align="stretch" spacing={3}>
            <Text><strong>Texto:</strong> {data.text}</Text>
            {data.icon && (
              <Box>
                <Text mb={2}><strong>Ícono:</strong></Text>
                <Image src={data.icon} alt="Icon" maxW="200px" borderRadius="md" />
              </Box>
            )}
            {data.link && <Text><strong>Enlace:</strong> <a href={data.link} target="_blank" rel="noopener noreferrer" style={{color: '#0066cc'}}>{data.link}</a></Text>}
          </VStack>
        );

      default:
        return (
          <Text>Tipo de recompensa no reconocido</Text>
        );
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completado';
      case 'failed':
        return 'Fallido';
      case 'pending':
        return 'Pendiente';
      default:
        return status;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Detalles de Asignación #{assignment.id}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack align="stretch" spacing={4}>
            <Box p={4} bg="gray.700" borderRadius="md">
              <Text fontSize="lg" fontWeight="bold" mb={2}>Información General</Text>
              <VStack align="stretch" spacing={2}>
                <Text><strong>Usuario:</strong> {assignment.userInfo.name} ({assignment.userInfo.email})</Text>
                <Text><strong>Nivel:</strong> {assignment.rewardInfo.level}</Text>
                <Text><strong>Recompensa:</strong> {assignment.rewardInfo.rewardTitle}</Text>
                <Text><strong>Tipo:</strong> {assignment.rewardInfo.rewardType}</Text>
                <HStack>
                  <Text><strong>Estado:</strong></Text>
                  <Badge colorScheme={getStatusColor(assignment.status)}>
                    {getStatusLabel(assignment.status)}
                  </Badge>
                </HStack>
                {assignment.assignedAt && (
                  <Text><strong>Asignado:</strong> {moment(assignment.assignedAt).format('DD/MM/YYYY HH:mm')}</Text>
                )}
                <Text><strong>Creado:</strong> {moment(assignment.createdAt).format('DD/MM/YYYY HH:mm')}</Text>
              </VStack>
            </Box>

            <Box p={4} bg="gray.700" borderRadius="md">
              <Text fontSize="lg" fontWeight="bold" mb={2}>Datos de Asignación</Text>
              {renderAssignmentData()}
            </Box>

            {assignment.errorMessage && (
              <Box p={4} bg="red.900" borderRadius="md" borderColor="red.500" borderWidth="1px">
                <Text fontSize="lg" fontWeight="bold" mb={2} color="red.200">Error</Text>
                <Text color="red.100">{assignment.errorMessage}</Text>
              </Box>
            )}
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const Actions = ({ 
  assignment, 
  onView,
  onRetrySuccess,
  retryAssignment
}: { 
  assignment: AdminLevelRewardAssignment;
  onView: () => void;
  onRetrySuccess: () => void;
  retryAssignment: (id: string) => Promise<void>;
}) => {
  const { missingPermission } = useRoles();
  const toast = useToast();
  const [isRetrying, setIsRetrying] = useState(false);

  const handleRetry = async () => {
    if (window.confirm('¿Estás seguro que deseas reintentar esta asignación?')) {
      setIsRetrying(true);
      try {
        await retryAssignment(assignment.id.toString());
        toast({
          title: 'Reintento exitoso',
          description: 'La asignación ha sido reintentada correctamente',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
        onRetrySuccess();
      } catch (error: any) {
        console.error("Error reintentando asignación:", error);
        toast({
          title: 'Error',
          description: error?.message || 'No se pudo reintentar la asignación',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      } finally {
        setIsRetrying(false);
      }
    }
  };

  const canEdit = !missingPermission(OlaggModule.LevelReward, PermissionEnum.EDIT);

  return (
    <Box display="flex" gap="10px" flexWrap="wrap">
      <Button 
        disabled={!canEdit} 
        colorScheme="blue"
        size="sm"
        onClick={onView}
      >
        Ver
      </Button>
      {assignment.status === 'failed' && (
        <Button 
          disabled={!canEdit || isRetrying} 
          colorScheme="orange"
          size="sm"
          onClick={handleRetry}
          isLoading={isRetrying}
          loadingText="Reintentando..."
        >
          Reintentar
        </Button>
      )}
    </Box>
  );
};

const RewardAssignments = () => {
  const navigate = useNavigate();
  const { missingPermission } = useRoles();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getLevelRewardAssignments, retryLevelRewardAssignment, loading } = useLevelRewardAssignments();
  const [assignments, setAssignments] = useState<AdminLevelRewardAssignment[]>([]);
  const [count, setCount] = useState(0);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedAssignment, setSelectedAssignment] = useState<AdminLevelRewardAssignment | null>(null);
  
  const [filter, setFilter] = useState({
    level: searchParams.get('level') || '',
    status: searchParams.get('status') || '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 10),
  });
  
  const { page, limit, level, status } = filter;

  const onFilter = ({
    name,
    value
  }: { name: string, value: any }) => {
    const parsedValue = (name === 'page' || name === 'limit') ? Number(value) : value;
    setFilter({ ...filter, [name]: parsedValue });

    if (value) searchParams.set(name, value);
    else searchParams.delete(name);
    setSearchParams(searchParams);
  };

  const onClearFilters = () => {
    setFilter({
      level: '',
      status: '',
      page: 1,
      limit: 10,
    });
    setSearchParams(new URLSearchParams());
  };

  const refreshAssignments = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const fetchAssignments = async () => {
    try {
      const response = await getLevelRewardAssignments({ 
        limit, 
        page, 
        level: level ? Number(level) : undefined,
        status: status ? status as 'pending' | 'completed' | 'failed' : undefined,
      });
      setAssignments(response.levelRewardAssignments);
      setCount(response.meta.count);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, [page, limit, level, status, refreshTrigger]);

  useEffect(() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlLevel = searchParams.get('level');
    const urlStatus = searchParams.get('status');

    if (urlPage || urlLimit || urlLevel || urlStatus) {
      setFilter({
        level: urlLevel || '',
        status: urlStatus || '',
        page: Number(urlPage || 1),
        limit: Number(urlLimit || 10),
      });
    }
  }, []);

  const handleViewAssignment = (assignment: AdminLevelRewardAssignment) => {
    setSelectedAssignment(assignment);
    onOpen();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'pending':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'completed':
        return 'Completado';
      case 'failed':
        return 'Fallido';
      case 'pending':
        return 'Pendiente';
      default:
        return status;
    }
  };

  const getRewardTypeLabel = (rewardType: string) => {
    switch (rewardType) {
      case 'xp_bonus':
        return 'Bonus XP';
      case 'blockchain_token':
        return 'Token Blockchain';
      case 'treasure_opportunity':
        return 'Oportunidad Tesoro';
      case 'claw_coin':
        return 'Moneda Garra';
      case 'store_discount':
        return 'Descuento Tienda';
      case 'custom_reward':
        return 'Recompensa Personalizada';
      default:
        return rewardType;
    }
  };

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full'>
        <Heading textAlign="center" mb={6}>
          Asignaciones de Premios por Nivel
        </Heading>

        {/* Inline filter */}
        <Box
          mb={6}
          p={4}
          bg="gray.800"
          borderRadius="md"
          border="1px"
          borderColor="gray.600"
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(4, 1fr)' }}
          gap={4}
        >
          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Nivel
            </Text>
            <Input
              placeholder="Buscar por nivel..."
              value={level}
              onChange={(e) => onFilter({ name: 'level', value: e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
              type="number"
            />
          </Box>

          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Estado
            </Text>
            <Select
              value={status}
              onChange={(e) => onFilter({ name: 'status', value: e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            >
              <option value="">Todos</option>
              <option value="pending">Pendiente</option>
              <option value="completed">Completado</option>
              <option value="failed">Fallido</option>
            </Select>
          </Box>

          <Box>
            <Text color="transparent" fontSize="sm" mb={1}>
              .
            </Text>
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={onClearFilters}
              size="sm"
              w="full"
            >
              Limpiar filtros
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          assignments?.length > 0 ? (
            <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
              <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                  <ChakraTable
                    variant="striped"
                    size="sm"
                    colorScheme="olaggPurple"
                    fontSize="18px"
                    position='relative'
                    whiteSpace={'normal'}
                  >
                    <Thead>
                      <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
                        <Th py={'20px'} color="white">ID</Th>
                        <Th py={'20px'} color="white">Usuario</Th>
                        <Th py={'20px'} color="white">Nivel</Th>
                        <Th py={'20px'} color="white">Recompensa</Th>
                        <Th py={'20px'} color="white">Tipo</Th>
                        <Th py={'20px'} color="white">Estado</Th>
                        <Th py={'20px'} color="white">Asignado</Th>
                        <Th py={'20px'} color="white">Acciones</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {assignments?.map((assignment) => (
                        <Tr key={assignment.id} color="white">
                          <Td py={'20px'}>{assignment.id}</Td>
                          <Td py={'20px'}>
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold">{assignment.userInfo.name}</Text>
                              <Text fontSize="sm" color="gray.400">{assignment.userInfo.email}</Text>
                            </VStack>
                          </Td>
                          <Td py={'20px'}>{assignment.rewardInfo.level}</Td>
                          <Td py={'20px'}>{assignment.rewardInfo.rewardTitle}</Td>
                          <Td py={'20px'}>
                            <Badge colorScheme="purple">
                              {getRewardTypeLabel(assignment.rewardInfo.rewardType)}
                            </Badge>
                          </Td>
                          <Td py={'20px'}>
                            <Badge colorScheme={getStatusColor(assignment.status)}>
                              {getStatusLabel(assignment.status)}
                            </Badge>
                          </Td>
                          <Td py={'20px'}>
                            {assignment.assignedAt 
                              ? moment(assignment.assignedAt).format('DD/MM/YYYY HH:mm')
                              : '-'
                            }
                          </Td>
                          <Td py={'20px'}>
                            <Actions 
                              assignment={assignment} 
                              onView={() => handleViewAssignment(assignment)}
                              onRetrySuccess={refreshAssignments}
                              retryAssignment={retryLevelRewardAssignment}
                            />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </ChakraTable>
                </TableContainer>
              </Flex>
            </Flex>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>No hay asignaciones cargadas</Text>
            </Box>
          )
        )}

        <Pagination
          params={{ limit, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={val =>
            onFilter({
              name: 'limit',
              value: val
            })
          }
          hideLimitInput
        />

        <AssignmentDetailsModal 
          assignment={selectedAssignment}
          isOpen={isOpen}
          onClose={onClose}
        />
      </Box>
    </Center>
  );
};

export default RewardAssignments;